import { Component, Input } from '@angular/core';

// ==== MODELS – match your API ====
interface ProductInfo {
  productId: string;
  code: string;
  name: string;
  size?: string;
  subItem?: any;
}

interface ProductStageItem {
  product: ProductInfo;
  qty: number;
  weight: number;
  changeInQty?: number;
  changeInWeight?: number;
  subItem?: any;
}

interface ProductDifferences {
  requested: ProductStageItem[];
  inTransit: ProductStageItem[]; // "Ready to dispatch"
  dispatched: ProductStageItem[];
  delivered: ProductStageItem[];
}

// Row used by the table
interface ProductRow {
  productId: string;
  productName: string;
  productSize?: string;

  // Inquiry
  requestedQty: number;
  requestedWeight: number;

  // Ready to dispatch (loaded)
  readyQty: number;
  readyWeight: number;
  readyQtyDiff?: number;
  readyWeightDiff?: number;

  // Dispatch
  dispatchedQty: number;
  dispatchedWeight: number;
  dispatchedQtyDiff?: number;
  dispatchedWeightDiff?: number;

  // Delivered
  deliveredQty: number;
  deliveredWeight: number;
  deliveredQtyDiff?: number;
  deliveredWeightDiff?: number;
}

@Component({
  selector: 'bst-frontend-view-product-diff',
  templateUrl: './view-product-diff.component.html',
  styleUrls: ['./view-product-diff.component.css'],
})
export class ViewProductDiffComponent {
  @Input() productDifferences!: ProductDifferences;

  rows: ProductRow[] = [];

  totals = {
    requestedQty: 0,
    requestedWeight: 0,
    readyQty: 0,
    readyWeight: 0,
    dispatchedQty: 0,
    dispatchedWeight: 0,
    deliveredQty: 0,
    deliveredWeight: 0,
  };

  ngOnChanges(): void {
    if (this.productDifferences) {
      this.buildRowsFromResponse(this.productDifferences);
    }
  }

  private buildRowsFromResponse(diffs: ProductDifferences): void {
    const map = new Map<string, ProductRow>();

    const makeKey = (item: ProductStageItem) => {
      const pid = item.product.productId ?? '';
      const sid = item.subItem?.productId ?? item.subItem?.name ?? '';
      return `${pid}::${sid}`;
    };

    const ensureRow = (item: ProductStageItem): ProductRow => {
      const key = makeKey(item);
      let row = map.get(key);
      if (!row) {
        row = {
          productId: item.product.productId,
          productName: item.product.name,
          productSize: item?.subItem?.name,
          requestedQty: 0,
          requestedWeight: 0,
          readyQty: 0,
          readyWeight: 0,
          dispatchedQty: 0,
          dispatchedWeight: 0,
          deliveredQty: 0,
          deliveredWeight: 0,
        };
        map.set(key, row);
      }
      return row;
    };

    // ---- Inquiry (requested) ----
    diffs.requested.forEach((item) => {
      const row = ensureRow(item);
      row.requestedQty = item.qty;
      row.requestedWeight = item.weight;
    });

    // ---- Ready to dispatch (loaded) ----
    diffs.dispatched.forEach((item) => {
      const row = ensureRow(item);
      row.readyQty = item.qty;
      row.readyWeight = item.weight;
      row.readyQtyDiff = item.changeInQty;
      row.readyWeightDiff = item.changeInWeight;
    });

    // ---- Dispatch ----
    diffs.inTransit.forEach((item) => {
      const row = ensureRow(item);
      row.dispatchedQty = item.qty;
      row.dispatchedWeight = item.weight;
      row.dispatchedQtyDiff = item.changeInQty;
      row.dispatchedWeightDiff = item.changeInWeight;
    });

    // ---- Delivered ----
    diffs.delivered.forEach((item) => {
      const row = ensureRow(item);
      row.deliveredQty = item.qty;
      row.deliveredWeight = item.weight;
      row.deliveredQtyDiff = item.changeInQty;
      row.deliveredWeightDiff = item.changeInWeight;
    });

    // convert map to array for *ngFor
    this.rows = Array.from(map.values());
    // ---- Totals ----
    this.totals = {
      requestedQty: 0,
      requestedWeight: 0,
      readyQty: 0,
      readyWeight: 0,
      dispatchedQty: 0,
      dispatchedWeight: 0,
      deliveredQty: 0,
      deliveredWeight: 0,
    };

    for (const r of this.rows) {
      this.totals.requestedQty += r.requestedQty;
      this.totals.requestedWeight += r.requestedWeight;

      this.totals.readyQty += r.readyQty;
      this.totals.readyWeight += r.readyWeight;

      this.totals.dispatchedQty += r.dispatchedQty;
      this.totals.dispatchedWeight += r.dispatchedWeight;

      this.totals.deliveredQty += r.deliveredQty;
      this.totals.deliveredWeight += r.deliveredWeight;
    }
  }

  // helpers for +/- and color
  diffClass(diff?: number): string {
    if (diff === undefined || diff === 0) return '';
    return diff > 0 ? 'text-success' : 'text-danger';
  }

  formatDiff(diff?: number): string {
    if (diff === undefined || diff === 0) return '';
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff}`;
  }
}
